/**
 * DEM / single-band elevation tiles look wrong in MapLibre / OpenLayers /
 * Giro3D unless TiTiler applies a contrast stretch (QGIS does this
 * automatically for Singleband Gray).
 *
 * Default look matches QGIS: `colormap_name=gray` + percentile `rescale` +
 * bilinear resampling. A multi-colour `terrain` ramp with nearest resampling
 * turns float DEM noise into rainbow "TV static".
 *
 * Performance contract (soft VMs / Canvas 2D):
 *   - Never scan the full COG. Statistics use a tiny downsampled window.
 *   - Callers may paint immediately with a provisional stretch, then refine.
 */

import type { Layer } from '@geosoft/viewer-sdk';
import { isDemLikeRaster } from '@/utils/titilerRasterBands';
import {
  applyRgbBandParams,
  applySingleBandSelection,
  extractCogQuery,
  fetchTitilerRasterProfile,
  isMultibandColorProfile,
  upsertQueryParam,
} from '@/utils/titilerRasterProfile';
import { ensureTitilerPngTileTemplate } from '@/utils/titilerSourceHost';
import { requestTileSize } from '@/utils/rasterTileResolution';

const rescaleCache = new Map<string, [number, number]>();

/**
 * Fixed render parameters per derived terrain product. Single definition for
 * every engine (Giro3D, MapLibre, Canvas 2D) so a companion COG looks the same
 * whichever renderer is active — and, critically, so its nodata sentinel is
 * always masked. An unmasked sentinel (`0` for hillshade, `-9999` for
 * slope/aspect) renders as an opaque black or white rectangle covering the
 * whole COG footprint, which reads as "the layer left its background behind"
 * once a companion above it is hidden.
 */
const TERRAIN_DERIVATIVE_RENDER: Readonly<
  Record<'hillshade' | 'slope' | 'aspect', {
    readonly colormap: string;
    /** Fixed domain — for products whose values are absolute (bytes, degrees). */
    readonly rescale?: readonly [number, number];
    /** Used until `/cog/statistics` returns; never the generic 0–100 DEM guess. */
    readonly fallbackRescale: readonly [number, number];
    readonly nodataFallback: number;
  }>
> = {
  // Shaded relief is already an 8-bit product, so its domain is absolute: a
  // percentile stretch over a narrow band (e.g. 151–197) blows the relief up
  // into salt-and-pepper noise. Keep the byte domain, as QGIS and Giro3D do.
  hillshade: { colormap: 'gray', rescale: [0, 255], fallbackRescale: [0, 255], nodataFallback: 0 },
  // Gentle terrain is a few degrees; 0–100 renders it uniformly black.
  slope: { colormap: 'viridis', fallbackRescale: [0, 45], nodataFallback: -9999 },
  aspect: { colormap: 'hsv', rescale: [0, 360], fallbackRescale: [0, 360], nodataFallback: -9999 },
};

/** Sentinels this low are nodata, never real data — see {@link sanitizeRescale}. */
const SENTINEL_FLOOR = -9998;

export type TerrainDerivativeColorRole = keyof typeof TERRAIN_DERIVATIVE_RENDER;

export function terrainDerivativeColorRole(
  layer: Pick<Layer, 'style'>,
): TerrainDerivativeColorRole | null {
  const role = layer.style?.['terrainDerivative'];
  return typeof role === 'string' && role in TERRAIN_DERIVATIVE_RENDER
    ? (role as TerrainDerivativeColorRole)
    : null;
}

export function terrainDerivativeRenderParams(
  role: TerrainDerivativeColorRole,
): (typeof TERRAIN_DERIVATIVE_RENDER)[TerrainDerivativeColorRole] {
  return TERRAIN_DERIVATIVE_RENDER[role];
}

/**
 * Reject a stretch that was computed over unmasked nodata. `/cog/statistics`
 * without `nodata=` reports `percentile_2 = -9999` for slope/aspect, which maps
 * every real value to pure white.
 */
function sanitizeRescale(range: readonly [number, number]): [number, number] | null {
  const [lo, hi] = range;
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || !(hi > lo)) return null;
  if (lo <= SENTINEL_FLOOR) return null;
  return [lo, hi];
}

function cachedRescale(tilesUrl: string, nodata?: number | null): [number, number] | null {
  const parts = extractCogQuery(tilesUrl);
  if (!parts) return null;
  return rescaleCache.get(`${parts.cogParam}::${nodata ?? ''}`) ?? null;
}

function readStyleNumber(layer: Pick<Layer, 'style'>, key: string): number | undefined {
  const value = layer.style?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

/**
 * Provisional stretch used so the first tiles paint without waiting on stats.
 * Prefer a meters-scale elevation range over 0–255 (byte) — coastal DEMs are
 * typically tens of metres, not byte-valued.
 */
export const PROVISIONAL_DEM_RESCALE: [number, number] = [0, 100];

/** QGIS-like singleband gray (not hypsometric terrain). */
export const DEM_DISPLAY_COLORMAP = 'gray';

export function isDemLikeLayer(layer: Pick<Layer, 'id' | 'kind' | 'label' | 'style' | 'endpoints'>): boolean {
  if (
    isDemLikeRaster({
      id: layer.id,
      kind: layer.kind,
      label: layer.label,
      title: typeof layer.style?.['title'] === 'string' ? layer.style['title'] : null,
      family: typeof layer.style?.['family'] === 'string' ? layer.style['family'] : null,
      artifactKind:
        typeof layer.style?.['gtwinArtifactKind'] === 'string'
          ? layer.style['gtwinArtifactKind']
          : typeof layer.style?.['artifactKind'] === 'string'
            ? layer.style['artifactKind']
            : null,
      terrainDerivative:
        typeof layer.style?.['terrainDerivative'] === 'string'
          ? layer.style['terrainDerivative']
          : null,
    })
  ) {
    return true;
  }
  // Fallback: already single-band TiTiler URL with no RGB bands.
  const tiles = layer.endpoints?.tiles ?? layer.endpoints?.terrain ?? '';
  return (
    typeof tiles === 'string'
    && tiles.includes('bidx=1')
    && !tiles.includes('bidx=2')
  );
}

export type DemColorParamsOptions = {
  readonly rescale?: readonly [number, number];
  /** When set, TiTiler masks this sentinel (transparent). */
  readonly nodata?: number | null;
  readonly colormap?: string;
};

/** Apply DEM colourisation query params (sync — no network). */
export function applyDemColorParams(
  tilesUrl: string,
  rescaleOrOpts: readonly [number, number] | DemColorParamsOptions = PROVISIONAL_DEM_RESCALE,
): string {
  const opts: DemColorParamsOptions = Array.isArray(rescaleOrOpts)
    ? { rescale: rescaleOrOpts as [number, number] }
    : (rescaleOrOpts as DemColorParamsOptions);
  const rescale = opts.rescale ?? PROVISIONAL_DEM_RESCALE;
  const colormap = opts.colormap ?? DEM_DISPLAY_COLORMAP;

  // Float DEMs as TiTiler's default JPEG look like TV static — always PNG.
  let out = ensureTitilerPngTileTemplate(tilesUrl);
  if (!out.includes('bidx=')) {
    out = upsertQueryParam(out, 'bidx', '1');
  }
  // Tile size tracks the display's pixel ratio so HiDPI screens get the COG at
  // physical resolution instead of an upscaled 256px PNG. Bilinear matches the
  // QGIS default read.
  out = upsertQueryParam(out, 'tilesize', String(requestTileSize()));
  out = upsertQueryParam(out, 'resampling', 'bilinear');
  out = upsertQueryParam(out, 'rescale', `${rescale[0]},${rescale[1]}`);
  out = upsertQueryParam(out, 'colormap_name', colormap);
  out = out.replace(/&bidx=2/g, '').replace(/&bidx=3/g, '');
  if (typeof opts.nodata === 'number' && Number.isFinite(opts.nodata)) {
    out = upsertQueryParam(out, 'nodata', String(opts.nodata));
  }
  return out;
}

/**
 * Synchronous, network-free colourisation for a single-band raster template —
 * used where the URL is baked before any engine mounts. Derived terrain
 * products get their role colormap and a masked nodata sentinel immediately, so
 * the very first painted frame cannot be an opaque footprint rectangle.
 */
export function applyRasterColorParams(
  tilesUrl: string,
  layer: Pick<Layer, 'id' | 'kind' | 'label' | 'style' | 'endpoints'>,
): string {
  const role = terrainDerivativeColorRole(layer);
  if (role) {
    const params = terrainDerivativeRenderParams(role);
    return applyDemColorParams(tilesUrl, {
      colormap: params.colormap,
      rescale: params.rescale ?? cachedRescale(tilesUrl) ?? params.fallbackRescale,
      nodata:
        readStyleNumber(layer, 'nodataValue')
        ?? readStyleNumber(layer, 'titilerNodata')
        ?? params.nodataFallback,
    });
  }
  if (!isDemLikeLayer(layer)) return tilesUrl;
  return applyDemColorParams(tilesUrl, {
    nodata: readStyleNumber(layer, 'nodataValue') ?? readStyleNumber(layer, 'titilerNodata') ?? null,
  });
}

/**
 * 2nd–98th percentile (or min/max) for a pleasing gray stretch.
 * Uses a tiny downsample (`max_size=48`) so TiTiler does not walk the COG.
 *
 * `nodata` must be passed whenever it is known: statistics computed over the
 * sentinel put `percentile_2` at `-9999`, which flattens every real pixel to
 * white (the "solid white box" on slope/aspect companions).
 */
export async function fetchTitilerRescaleRange(
  tilesUrl: string,
  nodata?: number | null,
): Promise<[number, number] | null> {
  const parts = extractCogQuery(tilesUrl);
  if (!parts) return null;
  const cacheKey = `${parts.cogParam}::${nodata ?? ''}`;
  const cached = rescaleCache.get(cacheKey);
  if (cached) return cached;

  // max_size keeps this a thumbnail-class read — critical for large DEMs.
  // Cold `/vsis3/` reads often exceed 2.5s; a short timeout left the first
  // paint on a bad provisional stretch until the user remounted the layer.
  const nodataQuery =
    typeof nodata === 'number' && Number.isFinite(nodata) ? `&nodata=${nodata}` : '';
  const statsUrl =
    `${parts.base}/cog/statistics?url=${parts.cogParam}&bidx=1&max_size=48${nodataQuery}`;
  try {
    const res = await fetch(statsUrl, { signal: AbortSignal.timeout(12000) });
    if (!res.ok) return null;
    const json = (await res.json()) as Record<
      string,
      { min?: number; max?: number; percentile_2?: number; percentile_98?: number }
    >;
    const band = Object.values(json)[0];
    if (!band) return null;
    const lo = band.percentile_2 ?? band.min;
    const hi = band.percentile_98 ?? band.max;
    if (typeof lo !== 'number' || typeof hi !== 'number') return null;
    const range = sanitizeRescale([lo, hi]);
    if (!range) return null;
    rescaleCache.set(cacheKey, range);
    return range;
  } catch {
    return null;
  }
}

/** Band nodata sentinel from `/cog/info` (cached with the rest of the profile). */
export async function fetchTitilerNodata(tilesUrl: string): Promise<number | null> {
  return (await fetchTitilerRasterProfile(tilesUrl))?.nodataValue ?? null;
}

/**
 * Rewrite a TiTiler XYZ template so single-band DEMs paint like QGIS
 * Singleband Gray (stretched grayscale) instead of rainbow static or a flat
 * black rectangle.
 *
 * By default waits briefly for a lightweight stats sample. Pass
 * `{ provisional: true }` to return immediately with a provisional stretch
 * (Canvas / soft-VM path) and refine later via {@link fetchTitilerRescaleRange}.
 */
export async function colorizeDemTileTemplate(
  tilesUrl: string,
  layer: Pick<Layer, 'id' | 'kind' | 'label' | 'style' | 'endpoints'>,
  options?: { provisional?: boolean },
): Promise<string> {
  const role = terrainDerivativeColorRole(layer);

  // `/cog/info` is a header read (cached per COG), unlike `/cog/statistics`.
  // The provisional path therefore still awaits it: painting one frame with the
  // wrong band selection shows a colour image in grayscale, and painting one
  // unmasked frame leaves an opaque footprint rectangle behind.
  const profile = await fetchTitilerRasterProfile(tilesUrl);

  // The file wins over the layer name. Terrain derivatives are single-band by
  // construction, so their role still decides.
  if (!role && isMultibandColorProfile(profile)) {
    return applyRgbBandParams(tilesUrl);
  }
  if (!role && !isDemLikeLayer(layer)) {
    return profile?.bandCount === 1 ? applySingleBandSelection(tilesUrl) : tilesUrl;
  }

  const nodata =
    readStyleNumber(layer, 'nodataValue')
    ?? readStyleNumber(layer, 'titilerNodata')
    ?? profile?.nodataValue
    ?? (role ? terrainDerivativeRenderParams(role).nodataFallback : null);

  if (role) {
    const params = terrainDerivativeRenderParams(role);
    const rescale =
      params.rescale
      ?? (options?.provisional ? null : await fetchTitilerRescaleRange(tilesUrl, nodata))
      ?? cachedRescale(tilesUrl, nodata)
      ?? params.fallbackRescale;
    return applyDemColorParams(tilesUrl, { rescale, nodata, colormap: params.colormap });
  }

  if (options?.provisional) {
    return applyDemColorParams(tilesUrl, {
      rescale: cachedRescale(tilesUrl, nodata) ?? PROVISIONAL_DEM_RESCALE,
      nodata,
    });
  }

  // Always re-apply: sceneItemToLayer may have baked a provisional stretch;
  // upgrade to percentile rescale (+ nodata) once stats are available.
  const rescale = (await fetchTitilerRescaleRange(tilesUrl, nodata)) ?? PROVISIONAL_DEM_RESCALE;
  return applyDemColorParams(tilesUrl, { rescale, nodata });
}
